/**
 * Advisory Service
 *
 * Firestore read/listen access to Strategic Advisory state:
 *   - `users/{userId}/strategic_advisory` — the engagements list
 *   - `users/{userId}/strategic_advisory/{projectId}` — the goal document
 *   - `users/{userId}/strategic_advisory/{projectId}/keyQuestions`
 *   - `users/{userId}/strategic_advisory/{projectId}/answers`
 *   - `users/{userId}/strategic_advisory/{projectId}/orchestration/{runId}`
 *   - `users/{userId}/strategic_advisory/{projectId}/facts/company`
 *   - `users/{userId}/strategic_advisory/{projectId}/facts/period`
 *   - `users/{userId}/v3_turns/{rootTurnId}` — S-05 research context
 *
 * There is no fourth `criteria` channel — no backend `criteria`
 * subcollection has ever existed to read, write or one-shot fetch.
 *
 * This module has no `deleteDoc` write surface. The live security rules
 * deny every client write on `strategic_advisory/**` by design (`order`/
 * `dependsOn` are a server-owned topological sort a client write could
 * corrupt), so a client-side delete would always be denied; removal goes
 * through the supervisor endpoint's `removeQuestions` field instead
 * (`useSupervisorTurn.ts`, the codebase's one permitted supervisor call
 * site) — `advisorySupervisorWireGuards.test.ts` asserts ZERO client
 * writes survive. The client also never CREATES a document — the frontend
 * never `setDoc`/`addDoc`s a key question, because the agent is the only
 * writer of adds. `fetchKeyQuestions` (the one-shot READ) is the
 * local-mode polling fallback's fetch primitive (`advisoryPolling.ts`),
 * and reads are permitted.
 *
 * Deliberate deviation from `conversationService.ts`'s `listenToConversation`
 * (lines 264-292): that function's error callback swallows the failure into
 * a console log and then calls its data callback with `null`, which makes a
 * denied read indistinguishable from a genuinely empty successful read.
 * Every listener function below therefore takes an explicit, separate
 * `onError` parameter, and the error path never calls the data callback.
 *
 * This service hands back RAW document data (`AdvisoryRawDoc`) and never
 * invokes a mapper inside an `onSnapshot` callback. The listener manager
 * built on top of this service is the single place a mapper throw is
 * caught — a mapper invoked here would sit outside that guard and could
 * silently kill the whole listener. The three mappers below are exported
 * so the manager can invoke them under its own try/catch, not so they can
 * be called here.
 *
 * The local-only polling loop itself (the `isActive`/`setTimeout` mechanics)
 * lives in `advisoryPolling.ts`, not here — this module supplies only the
 * one-shot `getDocs` read the poll loop calls on each tick.
 *
 * Whole-engagement removal is a soft delete through the authenticated gateway.
 * The backend retains the document and its subcollections with status "deleted";
 * list and goal listeners hide that terminal state before mapping it.
 */

import { collection, doc, getDoc, getDocs, onSnapshot, orderBy, query } from 'firebase/firestore';
import { auth, db } from './firebaseConfig';
import { ADVISORY_SUPERVISOR_ENDPOINT } from '../redux/slices/advisory/advisoryConfig';
import { UNKNOWN_ADVISORY_ERROR_CODE } from '../redux/slices/advisory/advisoryErrorCodes';
import type {
  AnalysisDoc,
  AnalysisDocStatus,
  CompanyFact,
  CounterpartiesFact,
  CounterpartyProfile,
  CounterpartyProfileStatus,
  DroppedCounterparty,
  AdvisoryAnswersDocStatus,
  AdvisoryAnswersRunDoc,
  EngagementAlternative,
  EngagementDoc,
  EngagementDocStatus,
  EngagementSummaryDoc,
  GoalAttempt,
  GoalDocStatus,
  KeyQuestionDoc,
  StrategicAdvisoryDoc,
} from '../redux/slices/advisory/advisoryTypes';

/**
 * One raw Firestore document, exactly as the SDK delivered it. Deliberately
 * untyped beyond `Record<string, unknown>` — validating and narrowing this
 * shape into a real document type is the mappers' job, guarded by the
 * listener manager, not this service's.
 */
export interface AdvisoryRawDoc {
  id: string;
  data: Record<string, unknown>;
}

/** The unsubscribe function every listener below returns. */
export type AdvisoryUnsubscribe = () => void;

/**
 * Thrown by the mappers when a document's shape does not match what its
 * status/type declares. Carries `docId` so the listener manager's catch
 * block can record which document failed without re-deriving it.
 */
export class AdvisoryMappingError extends Error {
  readonly docId: string;

  constructor(docId: string, message: string) {
    super(message);
    this.name = 'AdvisoryMappingError';
    this.docId = docId;
    // Restores the prototype chain broken by transpiling `extends Error`
    // through some TypeScript compile targets, so `instanceof
    // AdvisoryMappingError` reliably matches instances built at runtime.
    Object.setPrototypeOf(this, AdvisoryMappingError.prototype);
  }
}

/**
 * Extracts an error code from whatever `onSnapshot`'s error callback (or a
 * rejected `getDocs`/`deleteDoc` promise) delivers. Only the `code` property
 * crosses this boundary — the provider-authored `message` string never
 * does, so it can never reach state a component renders. A narrow,
 * structural check only: an object with a string `code` property uses it,
 * anything else falls back to the unknown code.
 *
 * Exported because `advisoryListeners.ts`'s local-mode poll error path
 * needs this exact extraction too (for a rejected `fetchKeyQuestions`
 * call) — a second implementation of the same narrow check is exactly the
 * drift this codebase avoids.
 */
export function extractErrorCode(error: unknown): string {
  if (
    error !== null &&
    typeof error === 'object' &&
    'code' in error &&
    typeof (error as { code: unknown }).code === 'string'
  ) {
    return (error as { code: string }).code;
  }
  return UNKNOWN_ADVISORY_ERROR_CODE;
}

/**
 * Listens to the engagements list: `users/{userId}/strategic_advisory`,
 * ordered by `updatedAt` descending. Delivers raw `{ id, data }` rows; an
 * empty result still flows through `onDocs` as `[]`, never through
 * `onError` — an empty list is a legitimate successful state, not a
 * failure.
 */
export function listenToEngagements(
  userId: string,
  onDocs: (docs: AdvisoryRawDoc[]) => void,
  onError: (errorCode: string) => void
): AdvisoryUnsubscribe {
  const engagementsRef = collection(db, `users/${userId}/strategic_advisory`);
  const engagementsQuery = query(engagementsRef, orderBy('updatedAt', 'desc'));

  return onSnapshot(
    engagementsQuery,
    querySnap => {
      onDocs(
        querySnap.docs
          .map(docSnap => ({
            id: docSnap.id,
            data: docSnap.data() as Record<string, unknown>,
          }))
          .filter(row => row.data.status !== 'deleted')
      );
    },
    error => {
      console.error('[advisoryService] listenToEngagements error:', error);
      onError(extractErrorCode(error));
    }
  );
}

/**
 * Listens to one goal document:
 * `users/{userId}/strategic_advisory/{projectId}`.
 *
 * `onDoc` receives `{ id, data }` with the raw document data untouched when
 * the document exists, or `null` when it does not — an absent document is
 * not an error, so `onError` is never called for that case. A retained
 * deleted document also delivers null, then signals `onDeleted` so callers
 * can distinguish it from a new engagement awaiting its first write. `onError` is
 * called only from the listener's own error path, and never with anything
 * but a code.
 */
export function listenToGoalDoc(
  userId: string,
  projectId: string,
  onDoc: (doc: AdvisoryRawDoc | null) => void,
  onError: (errorCode: string) => void,
  onDeleted?: () => void
): AdvisoryUnsubscribe {
  const goalDocRef = doc(db, `users/${userId}/strategic_advisory/${projectId}`);

  return onSnapshot(
    goalDocRef,
    docSnap => {
      if (docSnap.exists()) {
        const data = docSnap.data() as Record<string, unknown>;
        onDoc(data.status === 'deleted' ? null : { id: docSnap.id, data });
        if (data.status === 'deleted') onDeleted?.();
      } else {
        onDoc(null);
      }
    },
    error => {
      console.error(`[advisoryService] listenToGoalDoc error for ${projectId}:`, error);
      onError(extractErrorCode(error));
    }
  );
}

/**
 * Listens to the key-questions subcollection:
 * `users/{userId}/strategic_advisory/{projectId}/keyQuestions`.
 */
/**
 * Listen to one S-05 run's tracking document.
 *
 * `strategic_advisory/{projectId}/tracking/{runId}` is the ONLY live record of a run
 * in progress. The project document's `analysis` block is written exactly twice —
 * `completedTurns: 0` when the run begins and the final count when it ends — so it
 * reads "0 of 7" for the entire twenty minutes. This document is re-rolled after
 * every turn (`roll_totals`), which is what makes progress observable to a client
 * that did not start the run, or that reloaded.
 *
 * Client-readable and agent-svc-writable only, per the `tracking` blocks in
 * `infra/modules/firestore/rules/firestore.rules`.
 */
export function listenToRunTracking(
  userId: string,
  projectId: string,
  runId: string,
  onDoc: (doc: AdvisoryRawDoc | null) => void,
  onError: (errorCode: string) => void
): AdvisoryUnsubscribe {
  const runRef = doc(db, `users/${userId}/strategic_advisory/${projectId}/tracking/${runId}`);

  return onSnapshot(
    runRef,
    docSnap => {
      onDoc(
        docSnap.exists()
          ? { id: docSnap.id, data: docSnap.data() as Record<string, unknown> }
          : null
      );
    },
    // Same treatment as every other channel here: a denied read is reported as a
    // code, never swallowed into a null that reads as "no run".
    error => onError(error.code ?? UNKNOWN_ADVISORY_ERROR_CODE)
  );
}

/**
 * Listen to one run's per-turn records.
 *
 * Separate from the run document because they are read for different reasons and at
 * different rates: the run document carries the counters a progress bar needs, and
 * these carry the per-turn read-out. A client that only wants the count should not
 * pay for seven documents on every poll.
 */
export function listenToRunTurns(
  userId: string,
  projectId: string,
  runId: string,
  onDocs: (docs: AdvisoryRawDoc[]) => void,
  onError: (errorCode: string) => void
): AdvisoryUnsubscribe {
  const turnsRef = collection(
    db,
    `users/${userId}/strategic_advisory/${projectId}/tracking/${runId}/turns`
  );

  return onSnapshot(
    turnsRef,
    snap => onDocs(snap.docs.map(d => ({ id: d.id, data: d.data() as Record<string, unknown> }))),
    error => onError(error.code ?? UNKNOWN_ADVISORY_ERROR_CODE)
  );
}

export function listenToKeyQuestions(
  userId: string,
  projectId: string,
  onDocs: (docs: AdvisoryRawDoc[]) => void,
  onError: (errorCode: string) => void
): AdvisoryUnsubscribe {
  const keyQuestionsRef = collection(
    db,
    `users/${userId}/strategic_advisory/${projectId}/keyQuestions`
  );

  return onSnapshot(
    keyQuestionsRef,
    querySnap => {
      onDocs(
        querySnap.docs.map(docSnap => ({
          id: docSnap.id,
          data: docSnap.data() as Record<string, unknown>,
        }))
      );
    },
    error => {
      console.error(`[advisoryService] listenToKeyQuestions error for ${projectId}:`, error);
      onError(extractErrorCode(error));
    }
  );
}

/**
 * A supplied S-06 path is accepted only when it points back into the current
 * user's current project. This prevents an SSE payload from redirecting a
 * signed-in browser to another user's collection or to an unrelated project.
 */
export function validateAdvisoryAnswersPath(
  path: unknown,
  userId: string,
  projectId: string
): string | null {
  const expected = `users/${userId}/strategic_advisory/${projectId}/answers`;
  return path === expected ? expected : null;
}

export function validateAdvisoryOrchestrationPath(
  path: unknown,
  userId: string,
  projectId: string,
  runId: string
): string | null {
  const expected = `users/${userId}/strategic_advisory/${projectId}/orchestration/${runId}`;
  return path === expected ? expected : null;
}

/** Collection-level S-06 content listener. Document IDs stay separate from logical labels. */
export function listenToAdvisoryAnswers(
  path: string,
  onDocs: (docs: AdvisoryRawDoc[]) => void,
  onError: (errorCode: string) => void
): AdvisoryUnsubscribe {
  return onSnapshot(
    collection(db, path),
    snapshot =>
      onDocs(
        snapshot.docs.map(documentSnapshot => ({
          id: documentSnapshot.id,
          data: documentSnapshot.data() as Record<string, unknown>,
        }))
      ),
    error => onError(extractErrorCode(error))
  );
}

/** Durable S-06 run listener. No task/event/auction collections are subscribed to. */
export function listenToAdvisoryOrchestration(
  path: string,
  onDoc: (doc: AdvisoryRawDoc | null) => void,
  onError: (errorCode: string) => void
): AdvisoryUnsubscribe {
  return onSnapshot(
    doc(db, path),
    snapshot =>
      onDoc(
        snapshot.exists()
          ? { id: snapshot.id, data: snapshot.data() as Record<string, unknown> }
          : null
      ),
    error => onError(extractErrorCode(error))
  );
}

/**
 * A V3 turn id is a single Firestore path segment. It is persisted in the
 * project document's `analysis.rootTurnId`, but remains validated here before
 * it can form a new read path.
 */
const V3_TURN_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

export function advisoryV3TurnPath(userId: string, rootTurnId: string): string | null {
  if (!V3_TURN_ID_PATTERN.test(rootTurnId)) return null;
  return `users/${userId}/v3_turns/${rootTurnId}`;
}

/**
 * Read-only listener for the root V3 turn. The provenance reader deliberately
 * does not subscribe to its `nodes` subcollection: pgms persists the
 * de-duplicated `metadata.doc_intel` roll-up on this root document.
 */
export function listenToAdvisoryV3Turn(
  path: string,
  onDoc: (document: AdvisoryRawDoc | null) => void,
  onError: (errorCode: string) => void
): AdvisoryUnsubscribe {
  return onSnapshot(
    doc(db, path),
    snapshot =>
      onDoc(
        snapshot.exists()
          ? { id: snapshot.id, data: snapshot.data() as Record<string, unknown> }
          : null
      ),
    error => onError(extractErrorCode(error))
  );
}

/**
 * One-shot read of the key-questions subcollection — the local-mode poll's
 * fetch primitive (`advisoryPolling.ts`). Mirrors `listenToKeyQuestions`'s
 * collection path exactly, swapping `onSnapshot` for `getDocs`. A rejected
 * `getDocs` promise propagates uncaught: the poll loop owns the
 * `try`/`catch` (mirroring the `chatListeners.ts` analog), so this function
 * never swallows a rejection into an empty array.
 */
export async function fetchKeyQuestions(
  userId: string,
  projectId: string
): Promise<AdvisoryRawDoc[]> {
  const keyQuestionsRef = collection(
    db,
    `users/${userId}/strategic_advisory/${projectId}/keyQuestions`
  );
  const querySnap = await getDocs(keyQuestionsRef);
  return querySnap.docs.map(docSnap => ({
    id: docSnap.id,
    data: docSnap.data() as Record<string, unknown>,
  }));
}

/** Mark an engagement deleted through the gateway; no Firestore documents are removed. */
export async function deleteEngagement(userId: string, projectId: string): Promise<void> {
  const user = auth.currentUser;
  if (!user || user.uid !== userId) {
    throw Object.assign(new Error('Sign in to delete this engagement.'), {
      code: 'unauthenticated',
    });
  }
  const token = await user.getIdToken();
  let response: Response;
  try {
    response = await fetch(`${ADVISORY_SUPERVISOR_ENDPOINT}/${encodeURIComponent(projectId)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw Object.assign(new Error('The engagement could not be deleted.'), { code: 'unavailable' });
  }
  if (!response.ok) {
    const code =
      response.status === 401
        ? 'unauthenticated'
        : response.status === 403
        ? 'permission-denied'
        : response.status === 404
        ? 'not-found'
        : 'unavailable';
    throw Object.assign(new Error('The engagement could not be deleted.'), { code });
  }
  const result = await response.json();
  if (result?.projectId !== projectId || result?.status !== 'deleted') {
    throw Object.assign(new Error('Deletion was not confirmed.'), { code: 'unknown' });
  }
}

/** Every documented server status tag for the goal document. */
const GOAL_DOC_STATUSES: readonly GoalDocStatus[] = [
  'extracting',
  'review',
  'awaiting_confirmation',
  'confirmed',
  'failed',
];

function isGoalDocStatus(value: unknown): value is GoalDocStatus {
  return typeof value === 'string' && (GOAL_DOC_STATUSES as readonly string[]).includes(value);
}

/** Every documented status tag for the goal document's `engagement` sub-object. */
const ENGAGEMENT_DOC_STATUSES: readonly EngagementDocStatus[] = [
  'classifying',
  'classified',
  'engagement_failed',
];

function isEngagementDocStatus(value: unknown): value is EngagementDocStatus {
  return (
    typeof value === 'string' && (ENGAGEMENT_DOC_STATUSES as readonly string[]).includes(value)
  );
}

/**
 * Maps the goal document's raw `engagement` field to `EngagementDoc`, or
 * `null` when it is absent, not an object, or carries an unrecognised
 * `status`. Deliberately lenient rather than throwing — mirrors
 * `mapGoalDoc`'s own treatment of a missing/malformed `goal` array:
 * a document mid-confirm-turn (top-level `status: 'confirmed'`, no
 * `engagement` sub-object written yet) is an expected transient shape, not a
 * malformed one, so this must never fail the whole guarded mapping the way
 * an unrecognised top-level `status` does. Every string field is
 * typeof-guarded and defaulted, mirroring `useSupervisorTurn.ts`'s own
 * defensive parsing of the wire `engagement` event — this is untrusted
 * transport-shaped data either way, agent-authored text included, never
 * cast.
 */
function mapEngagementDoc(raw: unknown): EngagementDoc | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const data = raw as Record<string, unknown>;
  if (!isEngagementDocStatus(data.status)) {
    return null;
  }
  const rawAlternatives = Array.isArray(data.alternatives) ? data.alternatives : [];
  const alternatives: EngagementAlternative[] = rawAlternatives
    .filter((a): a is Record<string, unknown> => a !== null && typeof a === 'object')
    .map(a => ({
      engagementId: typeof a.engagementId === 'string' ? a.engagementId : '',
      name: typeof a.name === 'string' ? a.name : '',
      reason: typeof a.reason === 'string' ? a.reason : '',
    }));
  return {
    status: data.status,
    engagementId: typeof data.engagementId === 'string' ? data.engagementId : '',
    name: typeof data.name === 'string' ? data.name : '',
    description: typeof data.description === 'string' ? data.description : '',
    confidence: typeof data.confidence === 'string' ? data.confidence : '',
    rationale: typeof data.rationale === 'string' ? data.rationale : '',
    alternatives,
  };
}

/**
 * Maps the `facts/company` document, or `null` when it does not exist.
 *
 * Lenient on exactly `mapEngagementDoc`'s terms: a project created before S-02a
 * existed has no such document, and that is a normal shape rather than a corrupt
 * one. `null` leaves the wizard exactly where it was — no company gate, and the
 * orchestrator falls back to asking the corpus who its documents are about.
 */
export function mapCompanyFact(raw: Record<string, unknown> | null): CompanyFact | null {
  if (raw === null) {
    return null;
  }
  return {
    companyName: typeof raw.company_name === 'string' ? raw.company_name : '',
    confirmed: raw.confirmed === true,
    error: typeof raw.error === 'string' ? raw.error : '',
  };
}

/**
 * Maps the `facts/counterparties` document, or `null` when it does not exist.
 *
 * `null` is the normal shape for nineteen of the twenty-one engagement types —
 * they have no counterparty — and for any project that predates the stage. It
 * gates nothing.
 */
export function mapCounterpartiesFact(
  raw: Record<string, unknown> | null
): CounterpartiesFact | null {
  if (raw === null) {
    return null;
  }
  const companies = Array.isArray(raw.companies) ? raw.companies : [];
  return {
    companies: companies.filter((c): c is string => typeof c === 'string' && c.trim().length > 0),
    kind: typeof raw.kind === 'string' ? raw.kind : '',
    confirmed: raw.confirmed === true,
    error: typeof raw.error === 'string' ? raw.error : '',
    profiles: mapCounterpartyProfiles(raw.profiles),
    dropped: mapDroppedCounterparties(raw.dropped),
  };
}

/**
 * The `dropped` receipts off `facts/counterparties`.
 *
 * A receipt with no company name is discarded: the whole point of the record is
 * to name what left the engagement, and an anonymous one tells the user nothing
 * while still holding the notice open.
 */
function mapDroppedCounterparties(raw: unknown): DroppedCounterparty[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: DroppedCounterparty[] = [];
  for (const value of raw) {
    if (value === null || typeof value !== 'object') {
      continue;
    }
    const rec = value as Record<string, unknown>;
    const company = typeof rec.company === 'string' ? rec.company.trim() : '';
    if (company.length === 0) {
      continue;
    }
    out.push({
      company,
      reason: typeof rec.reason === 'string' ? rec.reason : '',
      sourceUrl: typeof rec.sourceUrl === 'string' ? rec.sourceUrl : '',
      lastStatus: typeof rec.lastStatus === 'string' ? rec.lastStatus : '',
      droppedAt: typeof rec.droppedAt === 'string' ? rec.droppedAt : '',
    });
  }
  return out;
}

/**
 * The `profiles` map off `facts/counterparties`.
 *
 * Every field is coerced rather than trusted, and an entry whose `status` is not
 * a known one is DROPPED: an unrecognised status would render as neither
 * in-flight nor finished, which is the one state the screen cannot show.
 */
function mapCounterpartyProfiles(raw: unknown): Record<string, CounterpartyProfile> {
  if (raw === null || typeof raw !== 'object') {
    return {};
  }
  const out: Record<string, CounterpartyProfile> = {};
  for (const [company, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value === null || typeof value !== 'object') {
      continue;
    }
    const rec = value as Record<string, unknown>;
    const status = rec.status;
    if (!isCounterpartyProfileStatus(status)) {
      continue;
    }
    out[company] = {
      company: typeof rec.company === 'string' ? rec.company : company,
      status,
      fileName: typeof rec.fileName === 'string' ? rec.fileName : '',
      chars: typeof rec.chars === 'number' ? rec.chars : 0,
      profile: typeof rec.profile === 'string' ? rec.profile : '',
      detail: typeof rec.detail === 'string' ? rec.detail : '',
      sourceUrl: typeof rec.sourceUrl === 'string' ? rec.sourceUrl : '',
    };
  }
  return out;
}

const COUNTERPARTY_PROFILE_STATUSES: readonly CounterpartyProfileStatus[] = [
  'researching',
  'ingested',
  'research_failed',
  'too_thin',
  'ingest_failed',
  'ingest_unavailable',
];

function isCounterpartyProfileStatus(value: unknown): value is CounterpartyProfileStatus {
  return (
    typeof value === 'string' &&
    (COUNTERPARTY_PROFILE_STATUSES as readonly string[]).includes(value)
  );
}

/** The one canonical company fact path for a signed-in user and project. */
export function advisoryCompanyFactPath(userId: string, projectId: string): string {
  return `users/${userId}/strategic_advisory/${projectId}/facts/company`;
}

/** The one canonical counterparties fact path for a signed-in user and project. */
export function advisoryCounterpartiesFactPath(userId: string, projectId: string): string {
  return `users/${userId}/strategic_advisory/${projectId}/facts/counterparties`;
}

/** Reads `strategic_advisory/{projectId}/facts/counterparties` ONCE. */
export async function getCounterpartiesFact(
  userId: string,
  projectId: string
): Promise<CounterpartiesFact | null> {
  const ref = doc(db, advisoryCounterpartiesFactPath(userId, projectId));
  const snap = await getDoc(ref);
  return mapCounterpartiesFact(snap.exists() ? (snap.data() as Record<string, unknown>) : null);
}

/**
 * Watches `strategic_advisory/{projectId}/facts/counterparties`.
 *
 * A LISTENER, not a one-shot read, and the profiles are the whole reason. The
 * list itself is written twice — extracted, then confirmed — and this tab's own
 * stream covers both. `profiles` is different: agent-svc writes one entry per
 * company as its research moves `researching -> ingested | research_failed`,
 * over 5-20 minutes per company, from a detached Cloud Run Job that keeps
 * running after the tab is closed. Those writes belong to nobody's stream once
 * the page has been reloaded, so a read fired at mount freezes the research
 * screen at whatever the states happened to be in that instant: reload with
 * `A: ingested, B: researching, C: queued` and it stays that way forever, with
 * a Retry that never becomes relevant and a Continue that never becomes
 * available.
 *
 * `dropped` moves on the same schedule and for the same reason.
 */
export function listenToCounterpartiesFact(
  userId: string,
  projectId: string,
  onFact: (fact: CounterpartiesFact | null) => void,
  onError: (errorCode: string) => void
): AdvisoryUnsubscribe {
  return onSnapshot(
    doc(db, advisoryCounterpartiesFactPath(userId, projectId)),
    snapshot =>
      onFact(
        mapCounterpartiesFact(
          snapshot.exists() ? (snapshot.data() as Record<string, unknown>) : null
        )
      ),
    error => onError(extractErrorCode(error))
  );
}

/**
 * Reads `strategic_advisory/{projectId}/facts/company` ONCE.
 *
 * A one-shot read rather than a listener, deliberately. The document changes
 * exactly twice in a project's life — once when the name is read and once when
 * the user confirms it — and both writes happen on a turn this client is already
 * streaming, so the SSE `company` / `companyConfirmed` events cover the live
 * case. A fourth Firestore channel to observe two writes would cost more than it
 * tells anyone. This read is what recovers the state on a reload.
 */
export async function getCompanyFact(
  userId: string,
  projectId: string
): Promise<CompanyFact | null> {
  const ref = doc(db, advisoryCompanyFactPath(userId, projectId));
  const snap = await getDoc(ref);
  return mapCompanyFact(snap.exists() ? (snap.data() as Record<string, unknown>) : null);
}

/**
 * Watches `strategic_advisory/{projectId}/facts/company`.
 *
 * The document really does change only twice, so unlike the counterparties
 * listener above this one buys little on its own. It exists so both facts the
 * company stage rests on arrive through ONE mechanism: a hook that listened to
 * one and polled the other would have two different staleness rules for two
 * documents written by the same turn, and the confirm-then-classify sequence
 * writes `company.confirmed` and `counterparties` close enough together that
 * the difference is observable.
 */
export function listenToCompanyFact(
  userId: string,
  projectId: string,
  onFact: (fact: CompanyFact | null) => void,
  onError: (errorCode: string) => void
): AdvisoryUnsubscribe {
  return onSnapshot(
    doc(db, advisoryCompanyFactPath(userId, projectId)),
    snapshot =>
      onFact(
        mapCompanyFact(snapshot.exists() ? (snapshot.data() as Record<string, unknown>) : null)
      ),
    error => onError(extractErrorCode(error))
  );
}

/** The one canonical reporting-period fact path for a signed-in user and project. */
export function advisoryPeriodFactPath(userId: string, projectId: string): string {
  return `users/${userId}/strategic_advisory/${projectId}/facts/period`;
}

/** Rejects any SSE path that does not identify this user's exact period fact. */
export function validateAdvisoryPeriodFactPath(
  path: unknown,
  userId: string,
  projectId: string
): string | null {
  const expected = advisoryPeriodFactPath(userId, projectId);
  return path === expected ? expected : null;
}

/** Read-only reporting-period listener used for reload recovery and confirmation races. */
export function listenToAdvisoryPeriodFact(
  path: string,
  onDoc: (document: AdvisoryRawDoc | null) => void,
  onError: (errorCode: string) => void
): AdvisoryUnsubscribe {
  return onSnapshot(
    doc(db, path),
    snapshot =>
      onDoc(
        snapshot.exists()
          ? { id: snapshot.id, data: snapshot.data() as Record<string, unknown> }
          : null
      ),
    error => onError(extractErrorCode(error))
  );
}

/** Server-owned unified review drafts; the path is derived from authenticated local identity. */
export function listenToAdvisoryReviewFact(
  userId: string,
  projectId: string,
  onDoc: (document: AdvisoryRawDoc | null, fromCache?: boolean) => void,
  onError: (errorCode: string) => void
): AdvisoryUnsubscribe {
  return onSnapshot(
    doc(db, `users/${userId}/strategic_advisory/${projectId}/facts/review`),
    { includeMetadataChanges: true },
    snapshot =>
      onDoc(
        snapshot.exists()
          ? { id: snapshot.id, data: snapshot.data() as Record<string, unknown> }
          : null,
        snapshot.metadata.fromCache
      ),
    error => onError(extractErrorCode(error))
  );
}

const ANALYSIS_DOC_STATUSES: readonly AnalysisDocStatus[] = [
  'prepared',
  'running',
  'completed',
  'partial',
  'analysis_failed',
];

function isAnalysisDocStatus(value: unknown): value is AnalysisDocStatus {
  return typeof value === 'string' && (ANALYSIS_DOC_STATUSES as readonly string[]).includes(value);
}

/**
 * Maps the goal document's raw `analysis` field to `AnalysisDoc`, or `null` when
 * it is absent, not an object, or carries an unrecognised `status`.
 *
 * Lenient rather than throwing, on exactly `mapEngagementDoc`'s terms above: a
 * confirmed project that has never run an analysis has no `analysis` sub-object
 * at all, and that is the common shape, not a malformed one.
 *
 * This is the reload-survival path for a run in flight. An engagement is ~13
 * minutes on the debug shape and hours on a full one, so by the time anyone
 * reloads the page the SSE stream is gone and this document is the only thing
 * that still says a run is going.
 *
 * `error` is present on the document and deliberately NOT mapped: it is raw
 * provider text, and this client never puts that into renderable state (ASVS V7)
 * — the same rule `useSupervisorTurn.ts` follows for the wire `error` event.
 * `status` answers whether the run failed, which is the renderable part.
 *
 * Counts default to `0` rather than throwing when absent. Unlike `maxIterations`
 * — where a defaulted `0` would render a misleading "0 of 0" refinement budget
 * and so throws — a run that has not written its counters yet is a real transient:
 * `begin_analysis` writes `completedTurns: 0` and `plannedTurns` together, and a
 * `prepared` run has neither by design.
 */
function mapAnalysisDoc(raw: unknown): AnalysisDoc | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const data = raw as Record<string, unknown>;
  if (!isAnalysisDocStatus(data.status)) {
    return null;
  }
  const str = (value: unknown): string => (typeof value === 'string' ? value : '');
  const num = (value: unknown): number => (typeof value === 'number' ? value : 0);
  return {
    status: data.status,
    runId: str(data.runId),
    conversationId: str(data.conversationId),
    docsCollectionId: str(data.docsCollectionId),
    internalCollectionId: str(data.internalCollectionId),
    rootTurnId: str(data.rootTurnId),
    nodeProfile: str(data.nodeProfile),
    debug: typeof data.debug === 'boolean' ? data.debug : false,
    plannedTurns: num(data.plannedTurns),
    completedTurns: num(data.completedTurns),
    failedTurns: num(data.failedTurns),
    // Empty-string timestamps are what `finish_analysis` writes before a run
    // ends, so they are normalised to `null` rather than surfaced as `''`.
    startedAt: str(data.startedAt) || null,
    completedAt: str(data.completedAt) || null,
  };
}

const ANSWERS_DOC_STATUSES: readonly AdvisoryAnswersDocStatus[] = [
  'answering',
  'answers_running',
  'answers_completed',
  'answers_partial',
  'answers_failed',
];

function mapAnswersRunDoc(raw: unknown): AdvisoryAnswersRunDoc | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const data = raw as Record<string, unknown>;
  if (
    typeof data.status !== 'string' ||
    !(ANSWERS_DOC_STATUSES as readonly string[]).includes(data.status)
  ) {
    return null;
  }
  const stringValue = (value: unknown): string => (typeof value === 'string' ? value : '');
  const numberValue = (value: unknown): number => (typeof value === 'number' ? value : 0);
  const rerunMode = stringValue(data.rerunMode);
  return {
    status: data.status as AdvisoryAnswersDocStatus,
    runId: stringValue(data.runId),
    rerunMode:
      rerunMode === 'resume' || rerunMode === 'retryGaps' || rerunMode === 'fresh' ? rerunMode : '',
    plannedItems: numberValue(data.plannedItems),
    answeredItems: numberValue(data.answeredItems),
    partialItems: numberValue(data.partialItems),
    unanswerableItems: numberValue(data.unanswerableItems),
    failedItems: numberValue(data.failedItems),
    startedAt: stringValue(data.startedAt) || null,
    completedAt: stringValue(data.completedAt) || null,
  };
}

/**
 * Maps a raw engagements-list row to `EngagementSummaryDoc`. Throws
 * `AdvisoryMappingError` when `status` is not one of the four documented
 * literals — the manager's guard routes that throw to the engagements
 * channel's failed state.
 *
 * Projects `goal`/`maxIterations`/`canRefine` using the IDENTICAL
 * extraction and validation logic `mapGoalDoc` above uses, so the two
 * mappers' validation guarantees never drift. `goal` defaults to `[]` when
 * missing or non-array. For `status === 'awaiting_confirmation'`, a missing
 * `maxIterations` (not `'number'`) throws rather than defaulting — a
 * silently-defaulted `maxIterations: 0` would render a misleading "0 of 0"
 * status line. `canRefine` is DERIVED, never read (see `mapGoalDoc` for the
 * disproof). Every other status maps absent values to inert `0`/`false`.
 *
 * Blast radius of this throw: `advisoryListeners.ts`'s `runGuardedMapping`
 * wraps the whole `rawDocs.map(mapEngagementSummaryDoc)` call, so ONE
 * malformed `awaiting_confirmation` row marks the ENTIRE engagements channel
 * `mapper-failed` rather than dropping just that row. That is the accepted,
 * deliberate trade-off — a wrong iteration cap must never render, and every
 * engagement passes through `awaiting_confirmation`, so this is not a rare
 * edge case.
 */
export function mapEngagementSummaryDoc(raw: AdvisoryRawDoc): EngagementSummaryDoc {
  const { data } = raw;
  if (!isGoalDocStatus(data.status)) {
    throw new AdvisoryMappingError(
      raw.id,
      `Unrecognised engagement status: ${String(data.status)}`
    );
  }
  const status = data.status;

  const goal: GoalAttempt[] = Array.isArray(data.goal) ? (data.goal as GoalAttempt[]) : [];

  if (status === 'awaiting_confirmation') {
    if (typeof data.maxIterations !== 'number') {
      throw new AdvisoryMappingError(
        raw.id,
        'awaiting_confirmation requires maxIterations on the document; a missing cap is never defaulted'
      );
    }
    return {
      id: raw.id,
      ...(typeof data.originalQuery === 'string' ? { originalQuery: data.originalQuery } : {}),
      status,
      title: typeof data.title === 'string' ? data.title : null,
      updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : null,
      goal,
      maxIterations: data.maxIterations,
      canRefine: goal.length < data.maxIterations,
    };
  }

  // Inert placeholder values: no stage derived from a non-awaiting_confirmation
  // status ever reads maxIterations or canRefine (see advisoryStage.ts).
  return {
    id: raw.id,
    ...(typeof data.originalQuery === 'string' ? { originalQuery: data.originalQuery } : {}),
    status,
    title: typeof data.title === 'string' ? data.title : null,
    updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : null,
    goal,
    maxIterations: typeof data.maxIterations === 'number' ? data.maxIterations : 0,
    canRefine: typeof data.canRefine === 'boolean' ? data.canRefine : false,
  };
}

/**
 * Maps a raw goal document to `StrategicAdvisoryDoc`.
 *
 * This mapper is not required to be total — the manager guards its call.
 * Two deliberate exceptions to "throw on anything unexpected":
 *
 * - A missing or non-array `goal` field becomes `[]`. The agent writes the
 *   document before its LLM call populates `goal`, so reading into an
 *   unpopulated array is the expected case during `extracting`, not an edge
 *   case.
 * - For every status except `awaiting_confirmation`, no derived stage reads
 *   `maxIterations`/`canRefine` (see `advisoryStage.ts`), so a missing value
 *   there maps to an inert `0`/`false` rather than throwing.
 *
 * `awaiting_confirmation` is the one status where the refine UI reads the cap
 * directly, so a missing `maxIterations` must never be silently defaulted —
 * a hardcoded cap could let the client offer a refinement the server would
 * refuse. That case still throws.
 *
 * `canRefine` does NOT, because it is not a document field and never was.
 * `advisory_state.py::AdvisoryState.to_doc()` enumerates every field that
 * reaches Firestore — `projectId`, `status`, `originalQuery`, `maxIterations`,
 * `confirmed`, `confirmedGoal`, `error`, `createdAt`, `goal`, `engagement` —
 * and `canRefine` is not among them; it is computed per-event on the SSE wire
 * only (`features/supervisor/service.py`: `state.iteration < max_iterations`,
 * where `state.iteration` is `len(goal[])`). Requiring it here threw on every
 * document the backend has ever written in this status, which is the first
 * status any user query produces. The same formula is reproduced below from
 * persisted state, so the invariant — never hardcode the cap — still holds:
 * `maxIterations` still comes from the document or throws.
 *
 * Also maps the document's `engagement` sub-object via `mapEngagementDoc`,
 * always explicitly (`null` when absent/malformed, never an omitted key) —
 * the sole consumer is the reload-recovery path in
 * `AdvisoryEngagementPage.tsx`, which renders it only when its own `status`
 * is `'classified'`.
 */
export function mapGoalDoc(raw: AdvisoryRawDoc): StrategicAdvisoryDoc {
  const { data } = raw;

  if (!isGoalDocStatus(data.status)) {
    throw new AdvisoryMappingError(
      raw.id,
      `Unrecognised goal document status: ${String(data.status)}`
    );
  }
  const status = data.status;

  const goal: GoalAttempt[] = Array.isArray(data.goal) ? (data.goal as GoalAttempt[]) : [];
  const updatedAt = typeof data.updatedAt === 'string' ? data.updatedAt : null;
  const engagement = mapEngagementDoc(data.engagement);
  const analysis = mapAnalysisDoc(data.analysis);
  const answers = mapAnswersRunDoc(data.answers);
  const originalQuery = typeof data.originalQuery === 'string' ? data.originalQuery : '';
  const confirmedGoal = typeof data.confirmedGoal === 'string' ? data.confirmedGoal : '';

  if (status === 'awaiting_confirmation') {
    if (typeof data.maxIterations !== 'number') {
      throw new AdvisoryMappingError(
        raw.id,
        'awaiting_confirmation requires maxIterations on the document; a missing cap is never defaulted'
      );
    }
    return {
      status,
      goal,
      maxIterations: data.maxIterations,
      canRefine: goal.length < data.maxIterations,
      updatedAt,
      engagement,
      analysis,
      answers,
      originalQuery,
      confirmedGoal,
      ...(data.workspaceVersion === 1 ? { workspaceVersion: 1 as const } : {}),
      ...(data.reviewWorkflowVersion === 1 ? { reviewWorkflowVersion: 1 as const } : {}),
    };
  }

  // Inert placeholder values: no stage derived from a non-awaiting_confirmation
  // status ever reads maxIterations or canRefine (see advisoryStage.ts).
  return {
    status,
    goal,
    maxIterations: typeof data.maxIterations === 'number' ? data.maxIterations : 0,
    canRefine: typeof data.canRefine === 'boolean' ? data.canRefine : false,
    updatedAt,
    engagement,
    analysis,
    answers,
    originalQuery,
    confirmedGoal,
    ...(data.workspaceVersion === 1 ? { workspaceVersion: 1 as const } : {}),
    ...(data.reviewWorkflowVersion === 1 ? { reviewWorkflowVersion: 1 as const } : {}),
  };
}

/**
 * Maps a raw key-questions-subcollection document to `KeyQuestionDoc`.
 * `subquestions` is omitted entirely when absent from the source document
 * (never defaulted to an empty array), and this mapper never reads or
 * returns `successCriteria`/`outOfScope`. Those are per-question fields the
 * backend stores but keeps deliberately off the SSE wire — they never
 * lived on a separate engagement-level criteria document; that document
 * never existed.
 *
 * Reads `data.question`/`data.subquestions` (lowercase q), not
 * `data.text`/`data.subQuestions` — the real document has never had a
 * `text` field.
 *
 * `order`: mapped whenever the document carries a numeric one, omitted
 * (never defaulted to `0`) otherwise — mirrors this function's existing
 * `subquestions` treatment. A real document always has it (one of the
 * eleven wire-carried fields), but the mapper stays defensive rather than
 * trusting that unconditionally. `advisorySlice.ts`'s
 * `compareKeyQuestionOrder` is what actually sequences the rendered list;
 * this mapper's only job is to carry the value through.
 */
export function mapKeyQuestionDoc(raw: AdvisoryRawDoc): KeyQuestionDoc {
  const { data } = raw;
  const mapped: KeyQuestionDoc = {
    id: raw.id,
    question: typeof data.question === 'string' ? data.question : '',
  };
  if (Array.isArray(data.subquestions)) {
    mapped.subquestions = data.subquestions as string[];
  }
  if (typeof data.order === 'number') {
    mapped.order = data.order;
  }
  if (typeof data.readOrder === 'number') {
    mapped.readOrder = data.readOrder;
  }
  if (typeof data.component === 'string') {
    mapped.component = data.component;
  }
  if (Array.isArray(data.catalogEngagements)) {
    mapped.catalogEngagements = data.catalogEngagements.filter(
      (value): value is string => typeof value === 'string'
    );
  }
  if (typeof data.matchedFromUserQuestion === 'string') {
    mapped.matchedFromUserQuestion = data.matchedFromUserQuestion;
  }
  if (typeof data.source === 'string') {
    mapped.source = data.source;
  }
  return mapped;
}

/** Server-owned workspace; clients only subscribe, all changes go through commands. */
export function listenToAdvisoryWorkspaceFact(
  userId: string,
  projectId: string,
  onDoc: (document: AdvisoryRawDoc | null, fromCache?: boolean) => void,
  onError: (errorCode: string) => void
): AdvisoryUnsubscribe {
  return onSnapshot(
    doc(db, `users/${userId}/strategic_advisory/${projectId}/facts/workspace`),
    { includeMetadataChanges: true },
    snapshot =>
      onDoc(
        snapshot.exists()
          ? { id: snapshot.id, data: snapshot.data() as Record<string, unknown> }
          : null,
        snapshot.metadata.fromCache
      ),
    error => onError(extractErrorCode(error))
  );
}
